import {useDispatch, useSelector} from "react-redux";
import {updateGamesGlobalFreeSpins, updateGameStudioFreeSpins} from "Redux/actions/freeSpins";
import {useRedPill} from "./api/useRedPill";
import {IS_DEV, IS_FREE_SPINS_ENABLED, IS_GAME_STUDIO_FREE_SPINS_ENABLED, IS_MOBILE, IS_QA3, MGS_FREE_SPINS_CONFIG, PLAYER_ID} from "Setup/config";
import {sendLogglyEvent} from "Helpers/loggly";
import useGames from "Hooks/useGames";

const findCgidByBundledGames = (bundledGames) => {
    const cidIndex = IS_MOBILE ? 1 : 0;
    let searchForGame = MGS_FREE_SPINS_CONFIG.find(g => g.mid === bundledGames.moduleId && g.cid[cidIndex] === bundledGames.clientId);

    if (searchForGame && searchForGame?.gcid) {
        console.log(searchForGame.gcid);
        return searchForGame.gcid;
    }
};

const useFreeSpins = () => {
    const {findGameByCatalogueGameId, isLobbyLoaded} = useGames();
    const dispatch = useDispatch();
    const {execute: executeGetGamesGlobalFreeSpins} = useRedPill("authorizedPaths.promotions.mgs-free-spins");
    const {execute: executeGetGameStudioFreeSpins} = useRedPill("authorizedPaths.promotions.gs-free-spins");

    // The offers are stored unfiltered and matched against the lobby when they're
    // read. Filtering during the fetch meant the result depended on whether the
    // smart lobby had landed first - and because the fetchers are called from
    // long-lived timers they kept the empty games list from the first render for
    // the life of the page, so free spins never appeared without a refresh.
    const getGamesGlobalFreeSpins = async () => {
        if (!IS_FREE_SPINS_ENABLED || IS_DEV || IS_QA3) return;

        let fsData = [];
        const res = await executeGetGamesGlobalFreeSpins({url: PLAYER_ID});

        try {
            const {code, data, redirect} = res;
            if (redirect) {
                window.location = redirect;
                return;
            }

            if (code === 200) {
                const freeSpinsData = data?.freeSpins;
                if (!freeSpinsData){
                    dispatch(updateGamesGlobalFreeSpins([]));
                    return;
                }

                for (let fs in freeSpinsData) {
                    let gameIds = [];
                    for (let g in freeSpinsData[fs].bundledGames) {
                        const bg = freeSpinsData[fs].bundledGames[g];
                        const gameId = findCgidByBundledGames(bg);

                        if (gameId && !gameIds.includes(gameId)) gameIds.push(gameId);
                    }

                    if (gameIds.length > 0) {
                        fsData.push({
                            gameIds,
                            offerInstances: freeSpinsData[fs].offerInstances
                        })
                    }
                }

                dispatch(updateGamesGlobalFreeSpins(fsData));
            }

            return true;
        } catch (e) {
            sendLogglyEvent('Free Spins', 'Error parsing free spins data', {error: e})
            console.log(e);
        }

        return false;
    };

    const getBroadwayStudioFreeSpins = async () => {
        if(!IS_GAME_STUDIO_FREE_SPINS_ENABLED) return;
        const res = await executeGetGameStudioFreeSpins();

        try {
            const {code, data: freeSpinsData, redirect} = res;
            if (redirect) {
                window.location = redirect;
                return;
            }

            if (code === 200) {
                if (!freeSpinsData){
                    dispatch(updateGameStudioFreeSpins([]));
                    return;
                }

                const grouped = Object.values(
                    freeSpinsData.reduce((acc, item) => {
                        const key = item.catalogueGameId;

                        if (!acc[key]) {
                            acc[key] = {
                                gameIds: item.gameIds,
                                catalogueGameId: item.catalogueGameId,
                                offerInstances: []
                            };
                        }

                        acc[key].offerInstances.push({
                            spinsAwarded: item.spinsAwarded,
                            spinsRemaining: item.spinsRemaining,
                            stakeValue: item.stakeValue,
                            expiryTime: item.expiryTime
                        });

                        return acc;
                    }, {})
                );

                dispatch(updateGameStudioFreeSpins(grouped));
            }

            return true;
        } catch (e) {
            sendLogglyEvent('Free Spins', 'Error parsing free spins data', {error: e})
            console.log(e);
        }

        return false;
    }

    const getUserFreeSpinsOffers = async () => {
        try {
            return await Promise.all([
                getGamesGlobalFreeSpins(),
                getBroadwayStudioFreeSpins()
            ]);
        } catch (error) {
            console.error(error);
            // throw error;
        }
    };

    const {gamesGlobalFreeSpinsGamesLoaded, gamesGlobalFreeSpinsGames, gameStudioFreeSpinsGamesLoaded, gameStudioFreeSpinsGames} = useSelector(state => ({
        gamesGlobalFreeSpinsGamesLoaded: state?.freeSpins?.gamesGlobal?.isLoaded ?? false,
        gamesGlobalFreeSpinsGames: state?.freeSpins?.gamesGlobal?.games ?? [],

        gameStudioFreeSpinsGamesLoaded: state?.freeSpins?.gameStudio?.isLoaded ?? false,
        gameStudioFreeSpinsGames: state?.freeSpins?.gameStudio?.games ?? []
    }));

    // Only show an offer for a game the player actually has in their lobby.
    const gamesGlobalGamesInLobby = gamesGlobalFreeSpinsGames.reduce((acc, offer) => {
        const gameIds = (offer?.gameIds ?? []).filter(gameId => findGameByCatalogueGameId(gameId));
        if (gameIds.length > 0) acc.push({...offer, gameIds});

        return acc;
    }, []);

    const gameStudioGamesInLobby = gameStudioFreeSpinsGames.filter(fs => findGameByCatalogueGameId(fs?.catalogueGameId));

    return {
        _getFreeSpins: getUserFreeSpinsOffers,

        // Not "loaded" until the lobby is here too, otherwise consumers would read
        // the pre-lobby filter result as "this player has no free spins".
        isGamesGlobalFreeSpinsLoaded: gamesGlobalFreeSpinsGamesLoaded && isLobbyLoaded,
        gamesGlobalFreeSpinsData: gamesGlobalGamesInLobby,

        isGameStudioFreeSpinsLoaded: gameStudioFreeSpinsGamesLoaded && isLobbyLoaded,
        gameStudioFreeSpinsData: gameStudioGamesInLobby
    };
}

export default useFreeSpins;
